import React, { useState, useEffect } from 'react';
import { getEducation, getCertifications, getSkills } from '../services/api';
import './About.css';

const About = () => {
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [eduRes, certRes, skillsRes] = await Promise.all([
          getEducation(),
          getCertifications(),
          getSkills()
        ]);
        
        // Ensure we're setting arrays even if the response is not an array
        setEducation(Array.isArray(eduRes.data) ? eduRes.data : []);
        setCertifications(Array.isArray(certRes.data) ? certRes.data : []);
        setSkills(Array.isArray(skillsRes.data) ? skillsRes.data : []);
        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Group skills by category with safe array handling
  const skillsByCategory = React.useMemo(() => {
    if (!Array.isArray(skills) || skills.length === 0) {
      return {};
    }

    return skills.reduce((acc, skill) => {
      const category = skill.category || 'other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(skill);
      return acc;
    }, {});
  }, [skills]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Oops! Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="btn btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="about-page">
      <div className="page-header">
        <h1>About Me</h1>
        <p>Full-Stack Developer & Designer from Uganda</p>
      </div>

      <div className="about-container">
        <div className="about-section">
          <div className="about-image" data-aos="fade-right">
            <img src="/sanga.png" alt="Sanga Ben" />
          </div>
          <div className="about-text" data-aos="fade-left">
            <h2>Who am I?</h2>
            <p>
              I'm Sanga Ben, a versatile technology professional based in Uganda, specializing in 
              full-stack development, graphic design, and web hosting solutions. With a strong 
              foundation in both technical and creative disciplines, I deliver comprehensive digital 
              solutions that combine functionality with aesthetic appeal.
            </p>
            <p>
              As an Information Systems graduate from Muni University, I approach projects with a 
              unique blend of technical expertise and creative problem-solving. Whether you need a 
              custom web application, eye-catching designs, or reliable hosting services, I provide 
              end-to-end solutions tailored to your business needs.
            </p>
            <div className="about-stats">
              <div className="stat">
                <span className="stat-number">3+</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat">
                <span className="stat-number">15+</span>
                <span className="stat-label">Projects Completed</span>
              </div>
              <div className="stat">
                <span className="stat-number">10+</span>
                <span className="stat-label">Happy Clients</span>
              </div>
            </div>
          </div>
        </div>

        <div className="skills-section">
          <h2 className="section-title">Technical Skills</h2>
          {Object.keys(skillsByCategory).length > 0 ? (
            <div className="skills-grid">
              {Object.entries(skillsByCategory).map(([category, categorySkills]) => (
                <div key={category} className="skill-category" data-aos="fade-up">
                  <h3>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                  <div className="skill-items">
                    {categorySkills.map(skill => (
                      <div key={skill.id} className="skill-item">
                        <div className="skill-info">
                          <span className="skill-name">{skill.name}</span>
                          <span className="skill-percentage">{skill.proficiency || 80}%</span>
                        </div>
                        <div className="skill-bar">
                          <div 
                            className="skill-progress" 
                            style={{ width: `${skill.proficiency || 80}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No skills data available</p>
          )}
        </div>

        <div className="education-section">
          <h2 className="section-title">Education</h2>
          {education.length > 0 ? (
            <div className="timeline">
              {education.map((edu, index) => (
                <div key={edu.id || index} className="timeline-item" data-aos="fade-up" data-aos-delay={index * 100}>
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h3>{edu.institution}</h3>
                    <p className="timeline-degree">{edu.degree}</p>
                    <p className="timeline-date">{edu.start_year} - {edu.end_year || 'Present'}</p>
                    {edu.description && <p className="timeline-description">{edu.description}</p>}
                    {edu.achievements && (
                      <div className="timeline-achievements">
                        <strong>Achievements:</strong>
                        <p>{edu.achievements}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No education data available</p>
          )}
        </div>

        <div className="certifications-section">
          <h2 className="section-title">Certifications & Leadership</h2>
          {certifications.length > 0 ? (
            <div className="certifications-grid">
              {certifications.map((cert, index) => (
                <div key={cert.id || index} className="certification-card" data-aos="fade-up" data-aos-delay={index * 100}>
                  <div className="certification-icon">
                    <i className="fas fa-certificate"></i>
                  </div>
                  <h3>{cert.title}</h3>
                  <p className="cert-issuer">{cert.issuer}</p>
                  <p className="cert-year">{cert.year}</p>
                  {cert.description && <p className="cert-description">{cert.description}</p>}
                  {cert.credential_url && (
                    <a href={cert.credential_url} target="_blank" rel="noopener noreferrer" className="cert-link">
                      View Credential
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No certifications data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default About;